import React, { Component } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';

import styles from './KovaStage.module.css';
import {
  calculateImageName,
  calcZIndex,
  calcLeftOffset,
  calcBottomOffset,
  determineLabelVis,
  createLabelList,
  generateImgSizeLoc,
  matchOptionLabelWithParent,
  findAllOptionLabelOverlaps,
  createOptionLabelLookup,
  removeOverlappedBaseLabels,
  handleOptionLabelOverlaps
} from '../../utils/FloorplanBuilder/FloorplanBuilder';

import { selectShowKovaStage } from '../../redux/app/app.selectors';
import { selectCommunityRID, selectCommunityDetails } from '../../redux/community/community.selectors';
import {
  selectModelRID,
  selectFloor,
  selectedOptions,
  selectShowElevation, selectConfigData
} from '../../redux/model/model.selectors';
import GlobalConfig from '../../utils/GlobalConfig';
import {findAllSelectedOptionsWithOptSels} from "../../redux/model/model.utils";

class KovaStage extends Component {
  state = {
    globalLeftOffset: 0,
    globalBottomOffset: 0,
    baseLabels: null,
    options: null,
    optionLabels: null,
    parentOptionLookup: null,
    optionLabelOverlaps: null,
    optionLabelLookup: null,
    baseImage: null,
    baseImageWidth: null,
    baseImageHeight: null,
    kovaScaleResult: null,
    kovaScaleSet: false,
    imgFolder: null,
    width: 0,
    height: 0
  };

  updateDimensions = () => {
    this.setState({ width: window.innerWidth, height: window.innerHeight });
  };

  componentDidMount() {
    const { selectedFloor, filteredOptImgs } = this.props;

    window.addEventListener('resize', this.updateDimensions);

    if (filteredOptImgs) this.compileImageData(filteredOptImgs, selectedFloor);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  componentDidUpdate(prevProps) {
    const { selectedFloor, filteredOptImgs } = this.props;

    if (selectedFloor !== prevProps.selectedFloor) {
      this.compileImageData(filteredOptImgs, selectedFloor);
    }

    if (!prevProps.filteredOptImgs) {
      this.compileImageData(filteredOptImgs, selectedFloor);
    }
  }

  reduceImgSize = (e, scaleFactor) => {
    const { baseImage, kovaScaleSet } = this.state;
    let kovaScaleResult = scaleFactor;
    const isBaseImg = Number(e.target.id) === baseImage.ObjectRID;

    // Dynamically determine correct scale to achieve a height of 700px;
    if (isBaseImg && !kovaScaleSet) {
      const maxHeight = 700;
      kovaScaleResult = Math.round((maxHeight / e.target.height) * 100) / 100;
    }

    // Apply the calculated scale to every image
    e.target.style.height = `${e.target.height * kovaScaleResult}px`;
    e.target.style.visibility = 'visible';

    /**
     * If current image is the base image and kova scale is not set in state
     * then set all 3 propertes in state
     *
     * Otherwise, the scale has already been determined, reset the value of
     * base width for the current floor so the floorplan
     * remains centered on the screen
     * */
    if (isBaseImg && !kovaScaleSet) {
      this.setState({
        baseImageWidth: String(e.target.offsetWidth),
        baseImageHeight: String(e.target.offsetHeight),
        kovaScaleResult,
        kovaScaleSet: true
      });
      return;
    }

    if (isBaseImg) {
      this.setState({
        baseImageWidth: String(e.target.offsetWidth),
        baseImageHeight: String(e.target.offsetHeight)
      });
    }
  };

  compileImageData = (filteredOptImgs, selectedFloor) => {
    const { modelRID, communityRID } = this.props;

    const imgFolder = `https://s3-${GlobalConfig.get(GlobalConfig.Key.S3_REGION)}.amazonaws.com/${
      GlobalConfig.get(GlobalConfig.Key.S3_KOVA_IFP)
    }/OptCfgImgs/${modelRID}/OptCfgImgs/`;

    const imgsByFloor = filteredOptImgs[selectedFloor];
    const baseImage = imgsByFloor.find(
      img => img.Type === 'Base'
    );

    // Aggregate each image type to pass into following functions
    const baseLabels = imgsByFloor.filter(img => img.Type === 'BaseLabel');
    // Hack for labels with negative height
    baseLabels.forEach((label) => {
      if (label.Height < 0) {
        label.Height *= -1;
      }
    });
    const options = imgsByFloor.filter(img => img.Type === 'Option');
    const optionLabels = imgsByFloor.filter(img => img.Type === 'OptionLabel');
    // Hack for labels with negative height
    optionLabels.forEach((label) => {
      if (label.Height < 0) {
        label.Height *= -1;
      }
    });
    const allLabels = baseLabels.concat(optionLabels);
    const allImgs = allLabels.concat(options);

    const locationData = generateImgSizeLoc(allImgs);
    const parentOptionLookup = matchOptionLabelWithParent(
      optionLabels,
      options
    );
    const optionLabelOverlaps = findAllOptionLabelOverlaps(
      optionLabels,
      allLabels,
      locationData
    );
    const optionLabelLookup = createOptionLabelLookup(options, optionLabels);

    this.setState({
      baseImage,
      baseLabels,
      options,
      optionLabels,
      parentOptionLookup,
      optionLabelOverlaps,
      optionLabelLookup,
      kovaScaleSet: false,
      imgFolder
    });
  };

  generateImgElement = (imgData, imgSrc) => {
    const {
      globalBottomOffset,
      globalLeftOffset,
      kovaScaleResult
    } = this.state;

    return (
      <img
        key={imgData.ObjectRID}
        id={imgData.ObjectRID}
        className={styles.kovaLayer}
        src={`${imgSrc}`}
        style={{
          bottom: `${calcBottomOffset(
            imgData,
            globalBottomOffset,
            kovaScaleResult
          )}px`,
          left: `${calcLeftOffset(
            imgData,
            globalLeftOffset,
            kovaScaleResult
          )}px`,
          zIndex: imgSrc.toLowerCase().includes('-base')
            ? -2147483646
            : calcZIndex(imgData) * -1,
          position: 'absolute',
          visibility: 'hidden',
          // outline: '2px solid blue'
        }}
        onLoad={e => this.reduceImgSize(e, kovaScaleResult)}
        alt=""
      />
    );
  }

  /**
   * Will always render the base Image first which ensures correct value for
   * kovaScaleResult is set in reduceImgSize.
   * */
  generateBaseImg = (selOptsWithCommId) => {
    const { filteredOptImgs, selectedFloor } = this.props;
    const { imgFolder } = this.state;

    const baseImg = filteredOptImgs[selectedFloor].find(img => img.Type === 'Base');
    const baseSrcImg = calculateImageName(
      baseImg,
      imgFolder,
      selOptsWithCommId
    );

    return this.generateImgElement(baseImg, baseSrcImg);
  };

  generateAllImgs = (selOptsWithCommId) => {
    const { filteredOptImgs, selectedFloor } = this.props;
    const { imgFolder } = this.state;

    const images = filteredOptImgs[selectedFloor].filter(img => img.Type === 'Option');

    return images.map((optImage) => {
      const srcImg = calculateImageName(
        optImage,
        imgFolder,
        selOptsWithCommId
      );

      if (srcImg) {
        return this.generateImgElement(optImage, srcImg);
      }
      return null;
    }).filter(Boolean);
  };

  generateAllText = (selOptsWithCommId) => {
    const { kovaScaleResult } = this.state;

    const {
      globalBottomOffset,
      globalLeftOffset,
      baseLabels,
      options,
      optionLabels,
      parentOptionLookup,
      optionLabelOverlaps,
      optionLabelLookup
    } = this.state;

    let associatedOptLabelRIDs = [];

    const optionsToDisplay = createLabelList(options, selOptsWithCommId);

    let optionLabelsToDisplay = createLabelList(optionLabels, selOptsWithCommId);

    // Generate array of all option label RIDs to display based on selected options
    optionsToDisplay.forEach((option) => {
      associatedOptLabelRIDs = associatedOptLabelRIDs.concat(
        optionLabelLookup[option.ObjectRID].relatedOptLabels
      );
    });

    const baseOverlappedLabelsRemoved = removeOverlappedBaseLabels(
      baseLabels,
      associatedOptLabelRIDs,
      optionLabelOverlaps
    );

    const resolvedOptLabelRIDs = new Set(
      handleOptionLabelOverlaps(
        associatedOptLabelRIDs,
        optionLabelOverlaps,
        parentOptionLookup
      )
    );

    optionLabelsToDisplay = optionLabelsToDisplay.filter(label =>
      resolvedOptLabelRIDs.has(label.ObjectRID));

    const newAgg = baseOverlappedLabelsRemoved.concat(optionLabelsToDisplay);

    return newAgg.map((optImage, index) => {
      const baseLength = newAgg[index].Description.length;
      let i = 0;
      const asciCheck = 10;
      const newDesc = newAgg[index].Description;
      let matchedVert = 0;
      let matchedHorizontal = 0;
      // const debug = false; // if set to true, floorplan will display filenames

      //  VAlign
      //  T = top - C = center - B = bottom

      //  HAlign
      //  L means left - R = right - C = center

      if (optImage.VAlign === 'T') {
        matchedVert = Number(optImage.Height * kovaScaleResult);
      } else if (optImage.VAlign === 'C') {
        matchedVert = Number((optImage.Height / 2) * kovaScaleResult);
      } else if (optImage.VAlign === 'M') {
        matchedVert = Number((optImage.Height / 2) * kovaScaleResult);
      } else if (optImage.VAlign === 'B') {
        matchedVert = 0;
      }

      if (optImage.HAlign === 'L') {
        matchedHorizontal = 0;
      } else if (optImage.HAlign === 'C') {
        matchedHorizontal = Number((optImage.Width / 2) * kovaScaleResult);
      } else if (optImage.HAlign === 'R') {
        matchedHorizontal = Number(optImage.Width * kovaScaleResult);
      }

      const replace = '\n';

      for (i = 0; i < baseLength; i += 1) {
        const asciiAtIndex = newAgg[index].Description.charCodeAt(i);
        if (asciiAtIndex === asciCheck) {
          newDesc.replace(/&#32/, replace);
        }
      }

      return (
        <p
          key={optImage.ObjectRID}
          className={styles.kovaLayer}
          id={optImage.ObjectRID}
          style={{
            bottom: `${calcBottomOffset(
              optImage,
              globalBottomOffset,
              kovaScaleResult
            ) - matchedVert}px`,
            left: `${Number(
              calcLeftOffset(optImage, globalLeftOffset, kovaScaleResult)
            ) - matchedHorizontal}px`,
            position: 'absolute',
            display: 'inline-block',
            height: `${optImage.Height * kovaScaleResult}px`,
            width: `${optImage.Width * kovaScaleResult}px`,
            zIndex:
              optImage.Type !== 'OptionLabel'
                ? calcZIndex(optImage)
                : calcZIndex(optImage) * -1,
            visibility: `${determineLabelVis(optImage, selOptsWithCommId)}`,
            fontSize: `${optImage.KY * kovaScaleResult}px`,
            whiteSpace: 'pre',
            textAlign: 'center',
            // outline: '2px solid red',
            transform: `rotate(-${optImage.Angle}deg)`,
            transformOrigin: 'top left'
          }}
          alt=""
        >
          {newDesc}
        </p>
      );
    });
  };

  generateKovaElevationImg = (selOptsWithCommId) => {
    const { filteredOptImgs, selectedFloor } = this.props;
    const { imgFolder } = this.state;

    const images = filteredOptImgs[selectedFloor];
    let conditionalSrcImg;

    images.forEach((optImage) => {
      const srcImg = calculateImageName(optImage, imgFolder, selOptsWithCommId);

      if (srcImg) conditionalSrcImg = srcImg;
    });

    return (
      <div className={styles.elevationImgContainer}>
        <img
          alt="Selected elevation"
          className={styles.kovaElevationImg}
          src={conditionalSrcImg}
        />
      </div>
    );
  };

  render() {
    const {
      showKovaStage,
      communityDetails,
      showElevation,
      filteredOptImgs,
      colorSelectorEnabled,
      configData
    } = this.props;

    function getScaling(width, height) {
      if (width <= 0) {
        width = window.innerWidth;
      }
      if (height <= 0) {
        height = window.innerHeight;
      }
      if (width > 1250 && height > 880) {
        return 1;
      } else if (width > 1150 && height > 750) {
        return 0.8;
      } else if (width > 1000 && height > 650) {
        return 0.6;
      } else if (width > 768 && height > 600) {
        return 0.5
      } else if (width > 768 && height > 400) {
        return 0.3
      }
      return 0.1;
    }

    const { options, baseImageWidth, baseImageHeight, kovaScaleSet, width, height } = this.state;

    const selOptsWithCommunityId = findAllSelectedOptionsWithOptSels(configData.SelectedOptions);

    if (communityDetails && communityDetails.CommunityID) {
      selOptsWithCommunityId.unshift(communityDetails.CommunityID);
    }

    return showKovaStage && filteredOptImgs ? (
      <div
        style={{
          width: `${!showElevation ? `${baseImageWidth}px` : '100%'}`,
          height: `${!showElevation ? `${baseImageHeight}px` : 'inherited'}`,
          transform: `scale(${getScaling(width, height)})`,
        }}
        className={`${!showElevation ? styles.kovaStage : styles.kovaElevation}`}
      >
        {selOptsWithCommunityId
          && options
          && !showElevation
          && this.generateBaseImg(selOptsWithCommunityId)}
        {selOptsWithCommunityId
          && options
          && !showElevation
          && kovaScaleSet
          && this.generateAllImgs(selOptsWithCommunityId).filter(Boolean)}
        {selOptsWithCommunityId
          && options
          && !showElevation
          && this.generateAllText(selOptsWithCommunityId)}
        {showElevation
          && !colorSelectorEnabled
          && this.generateKovaElevationImg(selOptsWithCommunityId)}
      </div>
    ) : null;
  }
}

const mapStateToProps = createStructuredSelector({
  showKovaStage: selectShowKovaStage,
  modelRID: selectModelRID,
  communityRID: selectCommunityRID,
  selectedFloor: selectFloor,
  communityDetails: selectCommunityDetails,
  showElevation: selectShowElevation,
  configData: selectConfigData
});

KovaStage.defaultProps = {
  filteredOptImgs: []
};

KovaStage.propTypes = {
  filteredOptImgs: PropTypes.array,
  modelRID: PropTypes.number.isRequired,
  communityRID: PropTypes.number.isRequired,
  selectedFloor: PropTypes.number.isRequired,
  showKovaStage: PropTypes.bool.isRequired,
  communityDetails: PropTypes.object.isRequired,
  showElevation: PropTypes.bool.isRequired,
  colorSelectorEnabled: PropTypes.bool.isRequired,
  configData: PropTypes.object.isRequired
};

export default connect(mapStateToProps)(KovaStage);
